import React, { useState, Component } from 'react';
import { FileText, Users, Image as ImageIcon, MessageSquareQuote, LayoutTemplate, Type, Bold } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { articles, sentimentOverTime, coverageByTier, topSources, spokespersonMentions, contentTypeLabels } from '../lib/data';
const COLORS = ['#0187c3', '#1A3A5C', '#64B5F6', '#0D5F8A', '#90CAF9', '#003F6B'];
function KPI({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-1195">
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-1196">
        <Icon className="h-4 w-4" data-id="element-1197" />
        {label}
      </div>
      <div className="text-3xl md:text-4xl font-semibold leading-none text-foreground" style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1198">
        {value}
      </div>
    </div>;
}
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-[#0187c3]';
    case 'neutral':
      return 'bg-[#64B5F6]';
    case 'negative':
      return 'bg-[#1A3A5C]';
    default:
      return 'bg-muted';
  }
}
export function Statistics() {
  const [activeTab, setActiveTab] = useState('panoramica');
  // Calculate KPIs
  const frontPageCount = articles.filter(a => a.frontPageCallout).length;
  const uniqueAuthors = new Set(articles.map(a => a.author)).size;
  const withPhotoCount = articles.filter(a => a.hasPhoto).length;
  const titleMentionCount = articles.filter(a => a.titleMentionsClient).length;
  const boldReferenceCount = articles.filter(a => a.boldedReference).length;
  // Calculate Content Type Distribution
  const contentTypeCounts = articles.reduce((acc, article) => {
    acc[article.contentType] = (acc[article.contentType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const contentTypeData = Object.entries(contentTypeCounts).map(([type, count]) => ({
    type,
    label: contentTypeLabels[type as keyof typeof contentTypeLabels],
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Top Authors
  const authorStats = articles.reduce((acc, article) => {
    if (!acc[article.author]) {
      acc[article.author] = {
        count: 0,
        totalScore: 0
      };
    }
    acc[article.author].count += 1;
    acc[article.author].totalScore += article.relevanceScore;
    return acc;
  }, {} as Record<string, {
    count: number;
    totalScore: number;
  }>);
  const topAuthorsList = Object.entries(authorStats).map(([name, stats]) => ({
    name,
    count: stats.count,
    avgScore: stats.totalScore / stats.count
  })).sort((a, b) => b.count - a.count).slice(0, 5);
  // Calculate Sections
  const sectionCounts = articles.reduce((acc, a) => {
    acc[a.section] = (acc[a.section] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const sectionData = Object.entries(sectionCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  // Calculate Geo Distribution (Mock mapping)
  const getRegion = (source: string) => {
    const s = source.toLowerCase();
    if (s.includes('messaggero')) return 'Lazio';
    if (s.includes('mattino')) return 'Campania';
    if (s.includes('gazzettino')) return 'Veneto';
    if (s.includes('nazione')) return 'Toscana';
    if (s.includes('secolo')) return 'Liguria';
    return 'Nazionale';
  };
  const geoCounts = articles.reduce((acc, a) => {
    const reg = getRegion(a.source);
    acc[reg] = (acc[reg] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const geoData = Object.entries(geoCounts).map(([name, count]) => ({
    name,
    count,
    percentage: count / articles.length * 100
  })).sort((a, b) => b.count - a.count);
  const maxTopSourceArticles = Math.max(...topSources.map(s => s.articles));
  // Reusable Components for duplicated sections across tabs
  const SentimentChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1199">
      <h3 className="text-lg font-semibold mb-6" data-id="element-1200">Sentiment nel tempo</h3>
      <div className="h-64" data-id="element-1201">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1202">
          <AreaChart data={sentimentOverTime} margin={{
          top: 5,
          right: 0,
          bottom: 0,
          left: -20
        }} data-id="element-1203">
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1204" />
            <XAxis dataKey="date" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1205" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1206" />
            <Tooltip data-id="element-1207" />
            <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1208" />
            <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1209" />
            <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1210" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const SectionsChart = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1211">
      <h3 className="text-lg font-semibold mb-6" data-id="element-1212">Distribuzione per sezione</h3>
      <div className="h-72" data-id="element-1213">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1214">
          <BarChart data={sectionData} margin={{
          top: 5,
          right: 0,
          left: -20,
          bottom: 5
        }} data-id="element-1215">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E5" data-id="element-1216" />
            <XAxis dataKey="name" tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} dy={10} data-id="element-1217" />
            <YAxis tick={{
            fontSize: 12
          }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1218" />
            <Tooltip cursor={{
            fill: 'rgba(0,0,0,0.05)'
          }} data-id="element-1219" />
            <Bar dataKey="count" fill="#0187c3" radius={[4, 4, 0, 0]} barSize={40} data-id="element-1220" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
  const TopAuthorsList = () => <div className="bg-card border border-border rounded-lg p-6" data-id="element-1221">
      <h3 className="text-lg font-semibold mb-6" data-id="element-1222">Top autori</h3>
      <div className="divide-y divide-border" data-id="element-1223">
        {topAuthorsList.map(author => <div key={author.name} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-1224">
            <div className="text-[15px] font-medium text-foreground truncate pr-4" data-id="element-1225">
              {author.name}
            </div>
            <div className="flex items-center gap-6 flex-shrink-0 text-right" data-id="element-1226">
              <div data-id="element-1227">
                <div className="font-semibold text-base" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1228">
                  {author.count}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-1229">
                  Articoli
                </div>
              </div>
              <div data-id="element-1230">
                <div className="font-semibold text-base text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1231">
                  {author.avgScore.toFixed(1)}
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider" data-id="element-1232">
                  Score
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-[0px] w-full" data-id="element-1233">
      {/* Header */}
      <div className="pt-6 md:pt-8 px-6 max-w-[1400px] mx-auto w-full bg-[#FFFFFF]" data-id="element-1234">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-id="element-1235">
          Statistiche
        </h1>
        <p className="text-base text-muted-foreground mb-6" data-id="element-1236">
          Analisi qualitativa della copertura mediatica del cliente
        </p>
      </div>

      {/* Tab bar - sticky */}
      <div className="border-b border-border sticky top-0 z-10 bg-background" data-id="element-1237">
        <div className="px-6 max-w-[1400px] mx-auto w-full flex items-end gap-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-[#FFFFFF]" data-id="element-1238">
          {[{
          id: 'panoramica',
          label: 'Panoramica'
        }, {
          id: 'contenuto',
          label: 'Contenuto'
        }, {
          id: 'posizionamento',
          label: 'Posizionamento'
        }, {
          id: 'fonti',
          label: 'Fonti & Autori'
        }, {
          id: 'sentiment',
          label: 'Sentiment'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="relative px-5 py-3 text-sm transition-colors whitespace-nowrap" style={{
          backgroundColor: activeTab === tab.id ? '#FFFFFF' : 'transparent',
          color: activeTab === tab.id ? 'var(--foreground)' : 'var(--muted-foreground)',
          fontWeight: activeTab === tab.id ? 500 : 300
        }} data-id="element-1239">
              {tab.label}
              {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0" style={{
            height: '2px',
            backgroundColor: '#0187c3'
          }} data-id="element-1240" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8 max-w-[1400px] mx-auto w-full" data-id="element-1241">
        <TabsContent value="panoramica" className="m-0 space-y-8" data-id="element-1242">
          {/* KPI Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1243">
            <KPI label="Prime pagine" value={frontPageCount} icon={FileText} data-id="element-1244" />
            <KPI label="Autori unici" value={uniqueAuthors} icon={Users} data-id="element-1245" />
            <KPI label="Con foto" value={withPhotoCount} icon={ImageIcon} data-id="element-1246" />
            <KPI label="Titolo con menzione" value={titleMentionCount} icon={MessageSquareQuote} data-id="element-1247" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1248">
            <SentimentChart data-id="element-1249" />

            {/* Coverage by Tier (Radar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1250">
              <h3 className="text-lg font-semibold mb-6" data-id="element-1251">Copertura per tier</h3>
              <div className="h-64" data-id="element-1252">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1253">
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={coverageByTier} data-id="element-1254">
                    <PolarGrid stroke="#E8E8E5" data-id="element-1255" />
                    <PolarAngleAxis dataKey="tier" tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} data-id="element-1256" />
                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} data-id="element-1257" />
                    <Radar name="Copertura" dataKey="percentage" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} data-id="element-1258" />
                    <Tooltip data-id="element-1259" />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contenuto" className="m-0 space-y-8" data-id="element-1260">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1261">
            {/* Content Type Distribution (Donut) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1262">
              <h3 className="text-lg font-semibold mb-6" data-id="element-1263">
                Distribuzione per tipologia
              </h3>
              <div className="h-72" data-id="element-1264">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1265">
                  <PieChart data-id="element-1266">
                    <Pie data={contentTypeData} innerRadius={70} outerRadius={100} paddingAngle={2} dataKey="count" nameKey="label" data-id="element-1267">
                      {contentTypeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1268" />)}
                    </Pie>
                    <Tooltip data-id="element-1269" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{
                    fontSize: '12px'
                  }} data-id="element-1270" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <SectionsChart data-id="element-1271" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1272">
            <TopAuthorsList data-id="element-1273" />
          </div>
        </TabsContent>

        <TabsContent value="posizionamento" className="m-0 space-y-8" data-id="element-1274">
          {/* Article Positioning */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1275">
            <h3 className="text-lg font-semibold mb-6" data-id="element-1276">
              Posizionamento articoli
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1277">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1278">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1279">
                  <LayoutTemplate className="h-5 w-5 text-muted-foreground" data-id="element-1280" />
                </div>
                <div data-id="element-1281">
                  <div className="text-2xl font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1282">
                    {frontPageCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5" data-id="element-1283">
                    Prima pagina
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1284">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1285">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" data-id="element-1286" />
                </div>
                <div data-id="element-1287">
                  <div className="text-2xl font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1288">
                    {withPhotoCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5" data-id="element-1289">
                    Con foto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1290">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1291">
                  <Bold className="h-5 w-5 text-muted-foreground" data-id="element-1292" />
                </div>
                <div data-id="element-1293">
                  <div className="text-2xl font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1294">
                    {boldReferenceCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5" data-id="element-1295">
                    Rif. in grassetto
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border" data-id="element-1296">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center flex-shrink-0" data-id="element-1297">
                  <Type className="h-5 w-5 text-muted-foreground" data-id="element-1298" />
                </div>
                <div data-id="element-1299">
                  <div className="text-2xl font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1300">
                    {titleMentionCount}
                  </div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5" data-id="element-1301">
                    Menzione titolo
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1302">
            <SectionsChart data-id="element-1303" />
          </div>
        </TabsContent>

        <TabsContent value="fonti" className="m-0 space-y-8" data-id="element-1304">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1305">
            {/* Top Sources (Horizontal Bar) */}
            <div className="bg-card border border-border rounded-lg p-6" data-id="element-1306">
              <h3 className="text-lg font-semibold mb-6" data-id="element-1307">Top testate</h3>
              <div className="h-80" data-id="element-1308">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1309">
                  <BarChart layout="vertical" data={topSources} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }} data-id="element-1310">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E8E8E5" data-id="element-1311" />
                    <XAxis type="number" hide data-id="element-1312" />
                    <YAxis dataKey="name" type="category" width={100} tick={{
                    fontSize: 12,
                    fill: '#8B8680'
                  }} axisLine={false} tickLine={false} data-id="element-1313" />
                    <Tooltip cursor={{
                    fill: 'rgba(0,0,0,0.05)'
                  }} data-id="element-1314" />
                    <Bar dataKey="articles" fill="#0187c3" radius={[0, 4, 4, 0]} barSize={20} name="Articoli" data-id="element-1315" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <TopAuthorsList data-id="element-1316" />

            {/* Geo Distribution */}
            <div className="bg-card border border-border rounded-lg p-6 lg:col-span-2" data-id="element-1317">
              <h3 className="text-lg font-semibold mb-6" data-id="element-1318">
                Distribuzione geografica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5" data-id="element-1319">
                {geoData.map(region => <div key={region.name} data-id="element-1320">
                    <div className="flex items-center justify-between mb-2 text-[15px]" data-id="element-1321">
                      <span className="text-foreground font-medium" data-id="element-1322">
                        {region.name}
                      </span>
                      <span className="text-muted-foreground" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1323">
                        {region.count} art. ({region.percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1324">
                      <div className="h-full bg-accent rounded-full transition-all" style={{
                    width: `${region.percentage}%`
                  }} data-id="element-1325" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sentiment" className="m-0 space-y-8" data-id="element-1326">
          <SentimentChart data-id="element-1327" />

          {/* Spokespersons */}
          <div className="bg-card border border-border rounded-lg p-6" data-id="element-1328">
            <h3 className="text-lg font-semibold mb-6" data-id="element-1329">Portavoce citati</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1330">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between p-4 border border-border rounded-md bg-muted/10" data-id="element-1331">
                  <div className="min-w-0 pr-4" data-id="element-1332">
                    <div className="text-[15px] font-medium text-foreground truncate" data-id="element-1333">
                      {person.name}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5" data-id="element-1334">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1335">
                    <span className="font-semibold text-lg" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1336">
                      {person.mentions}
                    </span>
                    <div className={`w-2.5 h-2.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-1337" />
                  </div>
                </div>)}
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>;
}